import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { deriveHierarchyFromShotCode, validateShotCode } from "./shared/shotCodeParser";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value: number, currency: string = "GBP") {
  // Ensure currency is a valid 3-letter code, fallback to GBP if empty or invalid
  const validCurrency = (currency && currency.length === 3) ? currency.toUpperCase() : "GBP";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: validCurrency,
    }).format(value);
  } catch (e) {
    console.warn(`Invalid currency code: ${currency}, falling back to GBP`);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "GBP",
    }).format(value);
  }
}

export function getCompanyLogoUrl(company: any): string | null {
  if (!company) return null;
  if (company.logo) return company.logo;
  if (company.domain) return `https://s2.googleusercontent.com/s2/favicons?domain=${company.domain}&sz=128`;
  if (company.website) {
    try {
      const url = new URL(company.website.startsWith('http') ? company.website : `https://${company.website}`);
      return `https://s2.googleusercontent.com/s2/favicons?domain=${url.hostname}&sz=128`;
    } catch (e) {
      // ignore
    }
  }
  return null;
}

export const getEpisodeFromShotCode = (shotCode: string) => {
  const validation = validateShotCode(shotCode);
  if (validation.isValid) {
    const hierarchy = deriveHierarchyFromShotCode(shotCode);
    if (hierarchy.seasonEpisodeCode) return hierarchy.seasonEpisodeCode;
  }
  const parts = shotCode.split('_');
  if (parts.length === 4) return parts[1];
  if (parts.length === 3) return parts[0];
  return "";
};

export const getSequenceFromShotCode = (shotCode: string) => {
  const validation = validateShotCode(shotCode);
  if (validation.isValid) {
    const hierarchy = deriveHierarchyFromShotCode(shotCode);
    if (hierarchy.sequenceNumber !== undefined) {
      return String(hierarchy.sequenceNumber).padStart(3, "0");
    }
  }
  const parts = shotCode.split('_');
  if (parts.length === 4) return parts[2];
  if (parts.length === 3) return parts[1];
  return "";
};

export const sortShots = (shotsToSort: any[]) => {
  return [...shotsToSort].sort((a, b) => {
    // Episode
    const epA = getEpisodeFromShotCode(a.shotCode);
    const epB = getEpisodeFromShotCode(b.shotCode);
    if (epA !== epB) return epA.localeCompare(epB);
    
    // Sequence
    const seqA = getSequenceFromShotCode(a.shotCode);
    const seqB = getSequenceFromShotCode(b.shotCode);
    if (seqA !== seqB) return seqA.localeCompare(seqB);
    
    // Shot
    const partsA = a.shotCode.split('_');
    const partsB = b.shotCode.split('_');
    const shotA = partsA.length === 4 ? partsA[3] : (partsA.length === 3 ? partsA[2] : a.shotCode);
    const shotB = partsB.length === 4 ? partsB[3] : (partsB.length === 3 ? partsB[2] : b.shotCode);
    
    return shotA.localeCompare(shotB);
  });
};
